import PropTypes from "prop-types";
import { useState } from "react";
import ReactApexChart from "react-apexcharts";
import {
  Box,
  Card,
  CardHeader,
  CircularProgress,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useChart } from "../chart";

const DAY_OPTIONS = [7, 14, 30, 60, 90];

// useChart's base palette only has 9 colors and ApexCharts cycles once a
// stacked chart has more series than that, so extend it to keep every
// category's segment visually distinct.
const CATEGORY_COLORS = [
  "#2065D1", "#FFC107", "#00B8D9", "#FF5630", "#36B37E",
  "#B76E00", "#7A0916", "#003768", "#5119B7", "#C684FF",
  "#006C9C", "#B71D18", "#0C68E9", "#FDA92D", "#22C55E",
];

CoinTransactionsStatsChart.propTypes = {
  stats: PropTypes.object,
  loading: PropTypes.bool,
  days: PropTypes.number,
  onDaysChange: PropTypes.func,
};

export default function CoinTransactionsStatsChart({
  stats,
  loading = false,
  days = 7,
  onDaysChange,
  height = 320,
  ...other
}) {
  const [tab, setTab] = useState("spent");

  const section = stats?.[tab];
  const series = section?.series ?? [];
  const labels = stats?.dates ?? [];
  const total = section?.total ?? 0;

  const chartOptions = useChart({
    colors: CATEGORY_COLORS,
    chart: {
      stacked: true,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        columnWidth: "45%",
        borderRadius: 2,
      },
    },
    stroke: { show: false },
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      itemMargin: { horizontal: 8, vertical: 4 },
    },
    labels,
    xaxis: { type: "datetime" },
    yaxis: {
      labels: {
        formatter: (value) => Number(value ?? 0).toLocaleString(),
      },
    },
    tooltip: {
      // Shared + intersect:false makes one hover list every category's slice
      // for that day. useChart hides the x label by default, so re-enable it.
      shared: true,
      intersect: false,
      x: {
        show: true,
        format: "dd MMM yyyy",
      },
      y: {
        formatter: (value) => Number(value ?? 0).toLocaleString(),
      },
    },
  });

  return (
    <Card sx={{ mb: 3 }} {...other}>
      <CardHeader
        title="Coins by Category"
        subheader={
          loading
            ? "Loading..."
            : `${total.toLocaleString()} coins ${
                tab === "spent" ? "spent" : "earned"
              } over the last ${days} days`
        }
        action={
          <TextField
            select
            size="small"
            label="Period"
            value={days}
            onChange={(event) => onDaysChange(Number(event.target.value))}
            sx={{ width: 130 }}
          >
            {DAY_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                Last {option} days
              </MenuItem>
            ))}
          </TextField>
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
        <Tabs value={tab} onChange={(event, value) => setTab(value)}>
          <Tab label="Coins Spent" value="spent" />
          <Tab label="Coins Earned" value="earned" />
        </Tabs>
      </Box>

      <Box sx={{ p: 3, pb: 1 }} dir="ltr">
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height,
            }}
          >
            <CircularProgress />
          </Box>
        ) : series.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height,
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              No coin activity in this period
            </Typography>
          </Box>
        ) : (
          <ReactApexChart
            // Remount on tab change so the stack animates from a clean state.
            key={tab}
            type="bar"
            series={series}
            options={chartOptions}
            height={height}
          />
        )}
      </Box>
    </Card>
  );
}
